/* Sanuro theme — front-end interactions
   ─────────────────────────────────────────────────────────────────
   Customizer-safe: every section attaches listeners/timers through
   a per-element cleanup registry that fires on shopify:section:unload.
   This prevents editor freezes after extended use.
   ───────────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  /* ============================================================
     Lifecycle infrastructure
     ============================================================ */

  // element → array of cleanup fns
  var cleanups = new Map();

  function onCleanup (el, fn) {
    if (!el || typeof fn !== 'function') return;
    var arr = cleanups.get(el);
    if (!arr) { arr = []; cleanups.set(el, arr); }
    arr.push(fn);
  }

  function runCleanupFor (el) {
    var fns = cleanups.get(el);
    if (!fns) return;
    for (var i = 0; i < fns.length; i++) {
      try { fns[i](); } catch (err) { /* swallow */ }
    }
    cleanups.delete(el);
  }

  // Walk the registry; run cleanups for any element inside (or equal to) `root`
  function cleanupSubtree (root) {
    if (!root) return;
    var toClean = [];
    cleanups.forEach(function (_fns, el) {
      if (el === root || (root.contains && root.contains(el)) || !document.body.contains(el)) {
        toClean.push(el);
      }
    });
    toClean.forEach(runCleanupFor);
  }

  // Idempotency marker: returns true if `el` was already inited for `key`
  function alreadyInit (el, key) {
    if (!el) return true;
    el._sanuroInit = el._sanuroInit || {};
    if (el._sanuroInit[key]) return true;
    el._sanuroInit[key] = true;
    return false;
  }

  // Helper: tracked setInterval (auto-cleared on cleanup of `scope`)
  function safeInterval (scope, fn, ms) {
    var id = setInterval(fn, ms);
    onCleanup(scope, function () { clearInterval(id); });
    return id;
  }

  // Helper: tracked addEventListener via AbortController
  function listen (scope, target, type, handler, opts) {
    var ctrl = new AbortController();
    var combined = Object.assign({}, opts || {}, { signal: ctrl.signal });
    target.addEventListener(type, handler, combined);
    onCleanup(scope, function () { ctrl.abort(); });
    return ctrl;
  }

  /* ============================================================
     Money formatter
     ============================================================ */
  function formatMoney (cents, format) {
    if (typeof cents === 'string') cents = parseInt(cents, 10);
    if (isNaN(cents)) cents = 0;
    var fmt = format || 'CHF {{amount}}';
    var amount = (cents / 100).toFixed(2).replace('.', ',');
    return fmt.replace(/\{\{\s*amount(?:_with_comma_separator)?\s*\}\}/, amount)
              .replace(/\{\{\s*amount_no_decimals(?:_with_comma_separator)?\s*\}\}/, Math.floor(cents / 100));
  }

  function escapeHtml (s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' })[c];
    });
  }

  /* ============================================================
     Announcement rotator
     ============================================================ */
  function initAnnouncementRotator (root) {
    (root || document).querySelectorAll('.announcement-bar--rotate').forEach(function (bar) {
      if (alreadyInit(bar, 'rotate')) return;
      var slides = bar.querySelectorAll('.announcement-rotator__slide');
      if (slides.length <= 1) return;
      var interval = parseInt(bar.getAttribute('data-interval'), 10) || 5000;
      var current = 0;
      var leavingTimers = [];

      safeInterval(bar, function () {
        // Defensive: bail if bar got detached
        if (!document.body.contains(bar)) return;
        var currentSlide = slides[current];
        var next = (current + 1) % slides.length;
        var nextSlide = slides[next];
        if (!currentSlide || !nextSlide) return;

        currentSlide.classList.remove('is-active');
        currentSlide.classList.add('is-leaving');

        // force reflow
        // eslint-disable-next-line no-unused-expressions
        nextSlide.offsetHeight;
        nextSlide.classList.add('is-active');

        var t = setTimeout(function () {
          currentSlide.classList.remove('is-leaving');
        }, 700);
        leavingTimers.push(t);

        current = next;
      }, interval);

      onCleanup(bar, function () {
        leavingTimers.forEach(function (t) { clearTimeout(t); });
      });
    });
  }

  /* ============================================================
     Mobile nav (one toggle/nav pair per page; safe to re-run)
     Supports: drawer-left, drawer-right, fullscreen styles, backdrop,
     close button, ESC key, and body scroll lock.
     ============================================================ */
  function initMobileNav (root) {
    var scope = root || document;
    var nav = document.querySelector('[data-mobile-nav]');
    if (!nav) return;
    var backdrop = document.querySelector('[data-mobile-backdrop]');

    function open () {
      nav.removeAttribute('hidden');
      // Force reflow so the .is-open transition runs
      // eslint-disable-next-line no-unused-expressions
      nav.offsetHeight;
      nav.classList.add('is-open');
      if (backdrop) {
        backdrop.removeAttribute('hidden');
        // eslint-disable-next-line no-unused-expressions
        backdrop.offsetHeight;
        backdrop.classList.add('is-open');
      }
      document.body.classList.add('is-mobile-nav-open');
      document.querySelectorAll('[data-nav-toggle]').forEach(function (t) {
        t.classList.add('is-open');
        t.setAttribute('aria-expanded', 'true');
      });
    }
    function close () {
      nav.classList.remove('is-open');
      if (backdrop) backdrop.classList.remove('is-open');
      document.body.classList.remove('is-mobile-nav-open');
      document.querySelectorAll('[data-nav-toggle]').forEach(function (t) {
        t.classList.remove('is-open');
        t.setAttribute('aria-expanded', 'false');
      });
      // After transition, fully hide
      setTimeout(function () {
        if (!nav.classList.contains('is-open')) nav.setAttribute('hidden', '');
        if (backdrop && !backdrop.classList.contains('is-open')) backdrop.setAttribute('hidden', '');
      }, 320);
    }

    var toggles = scope.querySelectorAll('[data-nav-toggle]');
    toggles.forEach(function (toggle) {
      if (alreadyInit(toggle, 'navtoggle')) return;
      listen(toggle, toggle, 'click', function () {
        if (nav.classList.contains('is-open')) close();
        else open();
      });
    });

    // Close button + link delegation — survives any future markup changes.
    if (!alreadyInit(nav, 'navClose')) {
      nav.addEventListener('click', function (e) {
        if (!e.target || !e.target.closest) return;
        if (e.target.closest('[data-nav-close]')) { close(); return; }
        if (e.target.closest('a')) { close(); /* default navigation continues */ }
      });
    }
    // Backdrop click closes
    if (backdrop && !alreadyInit(backdrop, 'navBackdrop')) {
      backdrop.addEventListener('click', close);
    }
    // ESC closes
    if (!alreadyInit(document, 'navEsc')) {
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && nav.classList.contains('is-open')) close();
      });
    }
  }

  /* ============================================================
     Quantity selector — document-delegated, init once
     ============================================================ */
  function initQuantity () {
    if (alreadyInit(document, 'qty')) return;
    document.addEventListener('click', function (e) {
      var dec = e.target.closest && e.target.closest('[data-qty-dec]');
      var inc = e.target.closest && e.target.closest('[data-qty-inc]');
      if (!dec && !inc) return;
      var wrap = (dec || inc).closest('.qty-selector');
      if (!wrap) return;
      var input = wrap.querySelector('input[type="number"]');
      if (!input) return;
      var value = parseInt(input.value, 10) || 0;
      var min = parseInt(input.min, 10) || 0;
      if (dec && value > min) value -= 1;
      if (inc) value += 1;
      input.value = value;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /* ============================================================
     Product gallery
     ============================================================ */
  function initProductGallery (root) {
    (root || document).querySelectorAll('[data-product-gallery]').forEach(function (gallery) {
      if (alreadyInit(gallery, 'gallery')) return;
      var slides = gallery.querySelectorAll('.product-gallery__slide');
      var thumbs = gallery.querySelectorAll('.product-gallery__thumb');
      var prev = gallery.querySelector('[data-gallery-prev]');
      var next = gallery.querySelector('[data-gallery-next]');
      var anim = gallery.getAttribute('data-gallery-anim') || 'slide';
      if (slides.length <= 1) return;

      var current = 0;
      function show (index, dir) {
        // dir: 1 = forward (slide right→left), -1 = backward (slide left→right)
        var outgoing = current;
        var incoming = index;
        if (anim === 'slide' && dir !== undefined) {
          // Set starting position of incoming slide
          var inFrom = dir > 0 ? '60px' : '-60px';
          var outTo   = dir > 0 ? '-60px' : '60px';
          slides[incoming].style.transform = 'translateX(' + inFrom + ')';
          slides[incoming].style.opacity = '0';
          slides[incoming].classList.add('is-active');
          // Force reflow
          void slides[incoming].offsetWidth;
          slides[incoming].style.transform = 'translateX(0)';
          slides[incoming].style.opacity = '1';
          if (slides[outgoing] && outgoing !== incoming) {
            slides[outgoing].style.transform = 'translateX(' + outTo + ')';
            slides[outgoing].style.opacity = '0';
            var out = slides[outgoing];
            setTimeout(function () {
              out.classList.remove('is-active');
              out.style.transform = '';
              out.style.opacity = '';
            }, 370);
          }
        } else {
          slides.forEach(function (s, i) { s.classList.toggle('is-active', i === incoming); });
        }
        thumbs.forEach(function (t, i) { t.classList.toggle('is-active', i === incoming); });
        current = incoming;
      }

      thumbs.forEach(function (thumb, i) {
        listen(gallery, thumb, 'click', function () { show(i, i > current ? 1 : -1); });
      });
      if (prev) listen(gallery, prev, 'click', function () {
        show((current - 1 + slides.length) % slides.length, -1);
      });
      if (next) listen(gallery, next, 'click', function () {
        show((current + 1) % slides.length, 1);
      });

      // Touch/swipe — only fires on clear horizontal swipe, ignores scroll
      var touchStartX = 0, touchStartY = 0;
      listen(gallery, gallery, 'touchstart', function (e) {
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
      }, { passive: true });
      listen(gallery, gallery, 'touchend', function (e) {
        var dx = e.changedTouches[0].clientX - touchStartX;
        var dy = e.changedTouches[0].clientY - touchStartY;
        if (Math.abs(dx) < 40) return;                  // too short = ignore
        if (Math.abs(dy) > Math.abs(dx) * 0.55) return; // diagonal/scroll = ignore
        if (dx < 0) show((current + 1) % slides.length, 1);
        else show((current - 1 + slides.length) % slides.length, -1);
      }, { passive: true });
    });
  }

  /* ============================================================
     Product bundles
     ============================================================ */
  function initBundles (root) {
    (root || document).querySelectorAll('[data-product-form]').forEach(function (form) {
      if (alreadyInit(form, 'bundles')) return;
      var bundles = form.querySelectorAll('.bundle');
      if (!bundles.length) return;
      var qtyInput = form.querySelector('[data-cart-quantity]');

      function sync () {
        bundles.forEach(function (bundle) {
          var radio = bundle.querySelector('input[type="radio"]');
          var isChecked = radio && radio.checked;
          bundle.classList.toggle('is-selected', !!isChecked);
          if (isChecked && qtyInput) {
            qtyInput.value = radio.getAttribute('data-qty') || radio.value || 1;
          }
          // Disable variant inputs in non-selected bundles.
          // Prevents name collisions: _color-1/_size-1 exist in all three bundles
          // but only the active one should be submitted with the form.
          bundle.querySelectorAll('.bundle__variant-sel, .bundle__chip-input').forEach(function (inp) {
            inp.disabled = !isChecked;
          });
        });
      }

      bundles.forEach(function (bundle) {
        listen(form, bundle, 'click', function (e) {
          var radio = bundle.querySelector('input[type="radio"]');
          if (!radio) return;
          if (e.target.tagName !== 'INPUT') radio.checked = true;
          sync();
        });
      });
      sync();
    });
  }

  /* ============================================================
     Bundle multi-add — adds each variant row as a separate cart item
     Toggle: Product information → Bundle — Varianten-Auswahl → "Jede Zeile als eigener Artikel"
     ============================================================ */
  function initBundleMultiAdd (root) {
    (root || document).querySelectorAll('[data-product-form][data-bundle-multi-add="true"]').forEach(function (form) {
      if (alreadyInit(form, 'bundleMultiAdd')) return;

      var variantScript = form.querySelector('[data-product-variants-json]');
      if (!variantScript) return;
      var variants = [];
      try { variants = JSON.parse(variantScript.textContent); } catch (e) { return; }

      function findVariantId (optValues) {
        for (var vi = 0; vi < variants.length; vi++) {
          var v = variants[vi];
          if (!v.options || v.options.length !== optValues.length) continue;
          var ok = true;
          for (var oi = 0; oi < optValues.length; oi++) {
            if (v.options[oi] !== optValues[oi]) { ok = false; break; }
          }
          if (ok) return v.id;
        }
        return null;
      }

      function handleSubmit (e) {
        var selectedBundle = form.querySelector('.bundle.is-selected');
        if (!selectedBundle) return; // no bundle selected — fall through

        var variantRows = selectedBundle.querySelectorAll('.bundle__variant-row');
        if (!variantRows.length) return; // no variant rows — normal submit handles it

        e.preventDefault();
        e.stopPropagation(); // stop initCartDrawer's document submit handler

        var items = [];
        variantRows.forEach(function (row) {
          var optVals = [];
          var selects = row.querySelectorAll('select.bundle__variant-sel:not([disabled])');
          var chips   = row.querySelectorAll('.bundle__chip-input:checked:not([disabled])');
          if (selects.length) {
            selects.forEach(function (s) { optVals.push(s.value); });
          } else if (chips.length) {
            chips.forEach(function (c) { optVals.push(c.value); });
          }
          if (!optVals.length) return;
          var id = findVariantId(optVals);
          if (id) items.push({ id: id, quantity: 1 });
        });

        // Append any active FBT items so they get added in the same request.
        if (window._sanuroCollectFbtItems) {
          var extras = window._sanuroCollectFbtItems(form);
          extras.forEach(function (it) { items.push(it); });
        }

        if (!items.length) { form.submit(); return; } // fallback

        var btn = form.querySelector('.product-form__add');
        var origText = btn ? btn.textContent.trim() : '';
        if (btn) { btn.disabled = true; btn.textContent = '…'; }

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: items })
        })
          .then(function (r) { return r.json(); })
          .then(function () {
            document.dispatchEvent(new CustomEvent('sanuro:cart:open'));
          })
          .catch(function () { form.submit(); })
          .finally(function () {
            if (btn) { btn.disabled = false; btn.textContent = origText; }
          });
      }

      listen(form, form, 'submit', handleSubmit);
    });
  }

  /* ============================================================
     Featured product — variant chip selector
     Toggle: Featured product → Card → "Varianten-Auswahl anzeigen"
     ============================================================ */
  function initFeaturedProductVariants (root) {
    (root || document).querySelectorAll('[data-fp-variants]').forEach(function (container) {
      if (alreadyInit(container, 'fpVariants')) return;

      var form = container.closest('form');
      if (!form) return;
      var hiddenId = form.querySelector('input[name="id"]');
      if (!hiddenId) return;

      var variantScript = container.querySelector('[data-fp-variants-json]');
      if (!variantScript) return;
      var variants = [];
      try { variants = JSON.parse(variantScript.textContent); } catch (e) { return; }

      function findVariantId (optValues) {
        for (var vi = 0; vi < variants.length; vi++) {
          var v = variants[vi];
          if (!v.options || v.options.length !== optValues.length) continue;
          var ok = true;
          for (var oi = 0; oi < optValues.length; oi++) {
            if (v.options[oi] !== optValues[oi]) { ok = false; break; }
          }
          if (ok) return v.id;
        }
        return null;
      }

      function updateVariant () {
        var optVals = [];
        container.querySelectorAll('.fp-opt-group').forEach(function (group) {
          var checked = group.querySelector('.fp-chip-input:checked');
          if (checked) optVals.push(checked.value);
        });
        if (!optVals.length) return;
        var id = findVariantId(optVals);
        if (id) hiddenId.value = String(id);
      }

      listen(container, container, 'change', updateVariant);
      updateVariant(); // initialise from default-checked inputs
    });
  }

  /* ============================================================
     Inline reviews slider — swipe + auto-rotate
     ============================================================ */
  function initInlineReviews (root) {
    (root || document).querySelectorAll('[data-reviews-slider]').forEach(function (rootEl) {
      if (alreadyInit(rootEl, 'reviews')) return;
      var track = rootEl.querySelector('.inline-reviews__track');
      var slides = rootEl.querySelectorAll('.inline-reviews__slide');
      var dotsWrap = rootEl.querySelector('[data-reviews-dots]');
      if (!slides.length || !track) return;

      var mode = (rootEl.getAttribute('data-mode') || 'both').toLowerCase();
      // 'auto' = only auto-rotate, 'swipe' = only manual, 'both' = both, 'none' = neither (still dots clickable)
      var interval = parseInt(rootEl.getAttribute('data-interval'), 10) || 6000;

      var current = 0;
      var autoTimer = null;

      if (dotsWrap) {
        dotsWrap.innerHTML = '';
        slides.forEach(function (_s, i) {
          var dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'inline-reviews__dot' + (i === 0 ? ' is-active' : '');
          dot.setAttribute('aria-label', 'Review ' + (i + 1));
          listen(rootEl, dot, 'click', function () { show(i); restartAuto(); });
          dotsWrap.appendChild(dot);
        });
      }

      function show (i) {
        if (!document.body.contains(track)) return;
        if (i < 0) i = slides.length - 1;
        if (i >= slides.length) i = 0;
        current = i;
        track.style.transform = 'translateX(' + (-i * 100) + '%)';
        if (dotsWrap) {
          dotsWrap.querySelectorAll('.inline-reviews__dot').forEach(function (d, di) {
            d.classList.toggle('is-active', di === i);
          });
        }
      }

      function startAuto () {
        if (autoTimer) clearInterval(autoTimer);
        if (mode !== 'auto' && mode !== 'both') return;
        autoTimer = setInterval(function () {
          if (!document.body.contains(rootEl)) return;
          show((current + 1) % slides.length);
        }, interval);
      }
      function restartAuto () { startAuto(); }

      onCleanup(rootEl, function () { if (autoTimer) clearInterval(autoTimer); });
      startAuto();

      // Swipe handling
      if (mode === 'swipe' || mode === 'both') {
        var startX = 0, startY = 0, deltaX = 0, deltaY = 0;
        var isDragging = false;
        var hasDirection = false;
        var swipeAxis = null; // 'x' or 'y'
        var trackWidth = 0;

        function onStart (e) {
          var touch = e.touches ? e.touches[0] : e;
          startX = touch.clientX;
          startY = touch.clientY;
          deltaX = 0;
          deltaY = 0;
          isDragging = true;
          hasDirection = false;
          swipeAxis = null;
          trackWidth = rootEl.clientWidth || 1;
          rootEl.classList.add('inline-reviews--dragging');
          if (autoTimer) { clearInterval(autoTimer); autoTimer = null; }
        }
        function onMove (e) {
          if (!isDragging) return;
          var touch = e.touches ? e.touches[0] : e;
          deltaX = touch.clientX - startX;
          deltaY = touch.clientY - startY;
          if (!hasDirection) {
            if (Math.abs(deltaX) > 8 || Math.abs(deltaY) > 8) {
              swipeAxis = Math.abs(deltaX) > Math.abs(deltaY) ? 'x' : 'y';
              hasDirection = true;
            }
          }
          if (swipeAxis === 'x') {
            if (e.cancelable && e.touches) e.preventDefault();
            var pct = (deltaX / trackWidth) * 100;
            track.style.transform = 'translateX(' + (-current * 100 + pct) + '%)';
          }
        }
        function onEnd () {
          if (!isDragging) return;
          isDragging = false;
          rootEl.classList.remove('inline-reviews--dragging');
          if (swipeAxis === 'x') {
            var threshold = trackWidth * 0.18;
            if (deltaX > threshold) show(current - 1);
            else if (deltaX < -threshold) show(current + 1);
            else show(current);
          }
          startAuto();
        }

        listen(rootEl, rootEl, 'touchstart', onStart, { passive: true });
        listen(rootEl, rootEl, 'touchmove',  onMove,  { passive: false });
        listen(rootEl, rootEl, 'touchend',   onEnd);
        listen(rootEl, rootEl, 'touchcancel', onEnd);
        listen(rootEl, rootEl, 'mousedown',  onStart);
        listen(rootEl, window, 'mousemove',  onMove);
        listen(rootEl, window, 'mouseup',    onEnd);
      }
    });
  }

  /* ============================================================
     Seamless infinite marquee — requestAnimationFrame driven.
     -----------------------------------------------------------
     We don't rely on CSS animations at all. A rAF loop drives
     translateX with a wrapping x value, so resize / address-bar
     changes / font swaps can never restart the position.
     The result is buttery-smooth on iOS Safari and Android Chrome.
     ============================================================ */
  function initMarquees (root) {
    (root || document).querySelectorAll('[data-marquee]').forEach(function (rootEl) {
      if (alreadyInit(rootEl, 'marquee')) return;
      var track = rootEl.querySelector('[data-marquee-track]');
      var group = rootEl.querySelector('[data-marquee-group]');
      var viewport = rootEl.querySelector('.marquee__viewport');
      if (!track || !group || !viewport) return;

      var groupW = 0;
      var pxPerSec = 0;
      var direction = 1; // 1 = scroll left, -1 = scroll right
      var x = 0;          // current px offset (always >= 0, < groupW)
      var lastTime = 0;
      var rafId = null;

      function readSpeed () {
        var speedSec = parseFloat(getComputedStyle(rootEl).getPropertyValue('--marquee-speed')) || 60;
        // The legacy CSS used "600 / speedSec" px/s — keep the same feel.
        pxPerSec = 600 / speedSec;
        if (pxPerSec < 8) pxPerSec = 8; // sane minimum
        var dir = (getComputedStyle(rootEl).getPropertyValue('--marquee-direction') || '').trim();
        direction = (dir === 'reverse') ? -1 : 1;
      }

      function rebuild () {
        if (!document.body.contains(rootEl)) return;
        // Reset to one original group, measure, then clone enough to fill viewport+1.
        track.innerHTML = '';
        track.appendChild(group);
        // Force layout
        // eslint-disable-next-line no-unused-expressions
        group.offsetWidth;
        var newGroupW = group.scrollWidth || 0;
        if (newGroupW < 1) return;

        // Preserve current visual position when group width changes
        if (groupW > 0) {
          x = (x / groupW) * newGroupW;
        }
        groupW = newGroupW;

        var viewportW = viewport.clientWidth || window.innerWidth || 0;
        // Need at least one full group offset off-screen, plus enough copies
        // to cover the viewport. Always at least 4 to be safe.
        var copies = Math.max(4, 2 + Math.ceil(viewportW / groupW));
        for (var i = 1; i < copies; i++) {
          var clone = group.cloneNode(true);
          clone.removeAttribute('data-marquee-group');
          clone.setAttribute('aria-hidden', 'true');
          track.appendChild(clone);
        }
        readSpeed();
      }

      function tick (now) {
        rafId = requestAnimationFrame(tick);
        if (!document.body.contains(rootEl)) return;
        if (document.hidden) { lastTime = now; return; }
        if (!lastTime) { lastTime = now; return; }
        var dt = (now - lastTime) / 1000;
        lastTime = now;
        if (dt > 0.25) dt = 0.016; // tab was inactive — don't jump
        x += pxPerSec * dt;
        if (groupW > 0) {
          // wrap into [0, groupW)
          if (x >= groupW) x -= groupW * Math.floor(x / groupW);
          if (x < 0) x += groupW * (Math.floor(-x / groupW) + 1);
        }
        var visualX = direction === 1 ? -x : (-(groupW - x));
        track.style.transform = 'translate3d(' + visualX.toFixed(2) + 'px, 0, 0)';
      }

      // Initial setup
      rebuild();
      // Make sure no leftover CSS animation is set (older versions used one).
      track.style.animation = 'none';
      lastTime = 0;
      rafId = requestAnimationFrame(tick);

      // Resize: only rebuild if width changed materially, then keep animating.
      var lastViewportW = viewport.clientWidth || window.innerWidth || 0;
      var rt = null;
      var onResize = function () {
        clearTimeout(rt);
        rt = setTimeout(function () {
          var w = viewport.clientWidth || window.innerWidth || 0;
          if (Math.abs(w - lastViewportW) <= 2) return;
          lastViewportW = w;
          rebuild();
        }, 200);
      };
      listen(rootEl, window, 'resize', onResize);
      onCleanup(rootEl, function () {
        if (rafId) cancelAnimationFrame(rafId);
        clearTimeout(rt);
      });

      // Web font swap can change group width — rebuild once when fonts are ready.
      if (document.fonts && document.fonts.ready && typeof document.fonts.ready.then === 'function') {
        document.fonts.ready.then(function () {
          if (document.body.contains(rootEl)) rebuild();
        });
      }
    });
  }

  /* ============================================================
     Before/after reveal slider (drag handle)
     ============================================================ */
  function initBeforeAfterSlider (root) {
    (root || document).querySelectorAll('[data-bas-slider]').forEach(function (slider) {
      if (alreadyInit(slider, 'basSlider')) return;
      var handle = slider.querySelector('[data-bas-handle]');
      if (!handle) return;
      var start = parseFloat(slider.getAttribute('data-start')) || 50;
      var pos = Math.max(0, Math.min(100, start));

      function setPos (p) {
        pos = Math.max(0, Math.min(100, p));
        slider.style.setProperty('--bas-pos', pos + '%');
        handle.setAttribute('aria-valuenow', String(Math.round(pos)));
      }
      setPos(pos);

      var dragging = false;
      function pointFromEvent (e) {
        var rect = slider.getBoundingClientRect();
        var clientX = (e.touches && e.touches[0]) ? e.touches[0].clientX : e.clientX;
        return ((clientX - rect.left) / rect.width) * 100;
      }
      function onDown (e) {
        dragging = true;
        slider.classList.add('is-dragging');
        setPos(pointFromEvent(e));
        if (e.cancelable) e.preventDefault();
      }
      function onMove (e) {
        if (!dragging) return;
        setPos(pointFromEvent(e));
      }
      function onUp () {
        dragging = false;
        slider.classList.remove('is-dragging');
      }
      function onKey (e) {
        var step = e.shiftKey ? 10 : 2;
        if (e.key === 'ArrowLeft')  { setPos(pos - step); e.preventDefault(); }
        if (e.key === 'ArrowRight') { setPos(pos + step); e.preventDefault(); }
        if (e.key === 'Home')       { setPos(0);   e.preventDefault(); }
        if (e.key === 'End')        { setPos(100); e.preventDefault(); }
      }

      listen(slider, slider, 'mousedown',  onDown);
      listen(slider, slider, 'touchstart', onDown, { passive: false });
      listen(slider, window, 'mousemove',  onMove);
      listen(slider, window, 'touchmove',  onMove, { passive: true });
      listen(slider, window, 'mouseup',    onUp);
      listen(slider, window, 'touchend',   onUp);
      listen(slider, window, 'touchcancel', onUp);
      listen(slider, handle, 'keydown',    onKey);
    });
  }

  /* ============================================================
     Before/after slider
     ============================================================ */
  function initBeforeAfter (root) {
    (root || document).querySelectorAll('[data-ba-slider]').forEach(function (slider) {
      if (alreadyInit(slider, 'ba')) return;
      var prev = slider.querySelector('[data-ba-prev]');
      var next = slider.querySelector('[data-ba-next]');
      var track = slider.querySelector('.before-after__track');
      if (!track) return;

      var amount = function () {
        var card = track.querySelector('.before-after__card');
        return card ? card.offsetWidth + (parseInt(getComputedStyle(track).gap, 10) || 18) : 300;
      };
      if (prev) listen(slider, prev, 'click', function () {
        track.scrollBy({ left: -amount(), behavior: 'smooth' });
      });
      if (next) listen(slider, next, 'click', function () {
        track.scrollBy({ left: amount(), behavior: 'smooth' });
      });
    });
  }

  /* ============================================================
     Review media lightbox — body-level, init once
     ============================================================ */
  function initReviewLightbox () {
    if (alreadyInit(document, 'reviewLightbox')) return;
    var lightbox = document.querySelector('[data-review-lightbox]');
    if (!lightbox) return;
    var inner = lightbox.querySelector('[data-lightbox-inner]');
    var closeBtn = lightbox.querySelector('[data-lightbox-close]');
    if (!inner) return;

    function open (type, src) {
      if (!src) return;
      if (type === 'video') {
        inner.innerHTML = '<iframe src="' + escapeHtml(src) + '" allow="autoplay; encrypted-media; picture-in-picture" allowfullscreen frameborder="0"></iframe>';
      } else {
        inner.innerHTML = '<img src="' + escapeHtml(src) + '" alt="">';
      }
      lightbox.removeAttribute('hidden');
      lightbox.setAttribute('aria-hidden', 'false');
      requestAnimationFrame(function () { lightbox.classList.add('is-open'); });
      document.body.style.overflow = 'hidden';
    }
    function close () {
      lightbox.classList.remove('is-open');
      lightbox.setAttribute('aria-hidden', 'true');
      setTimeout(function () {
        lightbox.setAttribute('hidden', '');
        if (inner) inner.innerHTML = '';
      }, 200);
      document.body.style.overflow = '';
    }

    document.addEventListener('click', function (e) {
      var thumb = e.target.closest && e.target.closest('[data-review-thumb]');
      if (!thumb) return;
      e.preventDefault();
      open(thumb.getAttribute('data-media-type'), thumb.getAttribute('data-media-src'));
    });
    if (closeBtn) closeBtn.addEventListener('click', close);
    lightbox.addEventListener('click', function (e) {
      if (e.target === lightbox) close();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && lightbox.classList.contains('is-open')) close();
    });
  }

  /* ============================================================
     Cart drawer — lives in layout, init once. Re-attach UI when DOM
     gets replaced by the customizer (header section reload).
     ============================================================ */
  function initCartDrawer () {
    var drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;
    if (alreadyInit(drawer, 'cartDrawer')) {
      // Drawer node still alive — just re-bind any new [data-cart-open] in newly loaded sections.
      bindCartOpeners();
      return;
    }

    var backdrop = document.querySelector('[data-cart-backdrop]');
    var body = drawer.querySelector('[data-cart-body]');
    var emptyState = drawer.querySelector('[data-cart-empty]');
    var lines = drawer.querySelector('[data-cart-lines]');
    var footer = drawer.querySelector('[data-cart-footer]');
    var subtotalEl = drawer.querySelector('[data-subtotal]');
    var savingsRow = drawer.querySelector('[data-cart-savings]');
    var savingsAmount = drawer.querySelector('[data-savings-amount]');
    var protectionEl = drawer.querySelector('[data-protection]');
    var protectionToggle = drawer.querySelector('[data-protection-toggle]');
    var moneyFormat = drawer.getAttribute('data-money-format') || 'CHF {{amount}}';
    var protectionVariantId = drawer.getAttribute('data-protection-variant') || '';
    var variantStyle = drawer.getAttribute('data-variant-style') || 'off';

    var protectionDefaultOn = false;
    try {
      var savedPref = localStorage.getItem('shippingProtectionPref');
      if (savedPref !== null) protectionDefaultOn = savedPref === '1';
      else protectionDefaultOn = (drawer.getAttribute('data-protection-default') === 'true');
    } catch (e) {
      protectionDefaultOn = (drawer.getAttribute('data-protection-default') === 'true');
    }

    /* ---- Variant compare_at_price cache ----
       Shopify's /cart.js does NOT expose compare_at_price on line items, so
       to show the strikethrough RRP + "Gespart" total we fetch each unique
       product's variants once via /products/{handle}.js and cache the result
       per variant_id. Subsequent renders use the cache. */
    var variantInfoCache = {};

    function ensureVariantInfo (items) {
      var fetches = [];
      var fetchedHandles = {};
      items.forEach(function (item) {
        if (variantInfoCache[item.variant_id] !== undefined) return;
        if (!item.handle) return;
        if (fetchedHandles[item.handle]) return;
        fetchedHandles[item.handle] = true;
        // Mark all referenced variants as "loading" with 0 so we don't retry.
        // The real value overwrites this below.
        variantInfoCache[item.variant_id] = 0;
        var p = fetch('/products/' + item.handle + '.js', {
          headers: { 'Accept': 'application/json' }
        })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (product) {
            if (!product || !product.variants) return;
            product.variants.forEach(function (v) {
              variantInfoCache[v.id] = v.compare_at_price || 0;
            });
          })
          .catch(function () { /* swallow */ });
        fetches.push(p);
      });
      return Promise.all(fetches);
    }

    function getCompareAtCents (item) {
      var v = variantInfoCache[item.variant_id];
      return (typeof v === 'number' && v > 0) ? v : 0;
    }

    function open () {
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      if (backdrop) {
        backdrop.removeAttribute('hidden');
        requestAnimationFrame(function () { backdrop.classList.add('is-open'); });
      }
      document.body.style.overflow = 'hidden';
      refresh();
    }
    function close () {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      if (backdrop) {
        backdrop.classList.remove('is-open');
        setTimeout(function () { backdrop.setAttribute('hidden', ''); }, 250);
      }
      document.body.style.overflow = '';
    }

    function parseAndTagStatus (r) {
      return r.json().then(function (data) {
        data._httpStatus = r.status;
        data._httpOk = r.ok;
        return data;
      });
    }
    function fetchCart () {
      return fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(parseAndTagStatus);
    }
    function cartChange (line, qty) {
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ line: line, quantity: qty })
      }).then(parseAndTagStatus);
    }
    function cartAdd (variantId, qty) {
      var idNum = parseInt(variantId, 10);
      if (!idNum || isNaN(idNum)) {
        return Promise.resolve({ _httpStatus: 0, _httpOk: false, description: 'Invalid variant ID: "' + variantId + '"' });
      }
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: idNum, quantity: qty || 1 })
      }).then(parseAndTagStatus);
    }

    function findProtectionLineIndex (cart) {
      if (!protectionVariantId) return -1;
      for (var i = 0; i < cart.items.length; i++) {
        if (String(cart.items[i].variant_id) === String(protectionVariantId)) return i + 1;
      }
      return -1;
    }

    function render (cart) {
      if (!cart || !cart.items) return;
      var protectionIdx = findProtectionLineIndex(cart);
      var realItems = cart.items.filter(function (item) {
        return String(item.variant_id) !== String(protectionVariantId);
      });

      document.querySelectorAll('[data-cart-count]').forEach(function (el) {
        el.textContent = realItems.reduce(function (s, i) { return s + i.quantity; }, 0);
      });

      if (realItems.length === 0) {
        if (emptyState) emptyState.removeAttribute('hidden');
        if (lines) lines.setAttribute('hidden', '');
        if (footer) footer.setAttribute('hidden', '');
        if (protectionEl) protectionEl.setAttribute('hidden', '');
        return;
      }

      if (emptyState) emptyState.setAttribute('hidden', '');
      if (lines) lines.removeAttribute('hidden');
      if (footer) footer.removeAttribute('hidden');

      if (lines) {
        lines.innerHTML = '';
        realItems.forEach(function (item) {
          var li = document.createElement('li');
          li.className = 'cart-drawer__line';
          li.setAttribute('data-line-key', item.key);
          var imgHtml = item.image
            ? '<img src="' + item.image.replace(/(\.[^.]+)$/, '_120x$1') + '" alt="">'
            : '';

          // "Original" line price = the highest plausible "you could have
          // paid" amount: max(compare_at_price × qty, original_line_price).
          // Captures both compare-at savings (per-unit RRP, fetched from the
          // variant via /products/...js) and line discounts. If neither
          // applies, no strikethrough is shown.
          var compareAtCents = getCompareAtCents(item);
          var compareLine = compareAtCents * (item.quantity || 1);
          var origLine = Math.max(compareLine, item.original_line_price || 0);
          var finalLine = item.final_line_price || 0;
          var showCompare = origLine > finalLine;
          var pricesHtml =
            (showCompare
              ? '<span class="cart-drawer__line-compare">' + formatMoney(origLine, moneyFormat) + '</span>'
              : '') +
            '<span class="cart-drawer__line-price">' + formatMoney(finalLine, moneyFormat) + '</span>';

          // Build variant / bundle display
          var variantHtml = '';
          if (variantStyle !== 'off') {
            // Collect bundle rows: properties named _optname-N (underscore-prefixed, hidden from checkout)
            var props = item.properties || {};
            var bundleRows = {};
            var hasBundleProps = false;
            Object.keys(props).forEach(function (key) {
              if (key.charAt(0) !== '_') return;
              var k = key.slice(1); // strip _
              var m = k.match(/^(.+?)-([0-9]+)$/);
              if (!m) return;
              hasBundleProps = true;
              var rowNum = m[2];
              if (!bundleRows[rowNum]) bundleRows[rowNum] = [];
              bundleRows[rowNum].push(escapeHtml(String(props[key])));
            });

            if (hasBundleProps) {
              // Multiple bundle rows — show each separately
              var rowNums = Object.keys(bundleRows).sort(function (a, b) { return +a - +b; });
              if (variantStyle === 'chips') {
                variantHtml = '<div class="cart-drawer__bundle-rows">' +
                  rowNums.map(function (rn) {
                    return '<div class="cart-drawer__bundle-row">' +
                      '<span class="cart-drawer__variant-chip cart-drawer__bundle-num">#' + rn + '</span>' +
                      bundleRows[rn].map(function (v) {
                        return '<span class="cart-drawer__variant-chip">' + v + '</span>';
                      }).join('') + '</div>';
                  }).join('') + '</div>';
              } else {
                variantHtml = rowNums.map(function (rn) {
                  return '<p class="cart-drawer__line-variant">#' + rn + ': ' + bundleRows[rn].join(' · ') + '</p>';
                }).join('');
              }
            } else if (item.variant_title && item.variant_title !== 'Default Title') {
              // Single variant (non-bundle product)
              if (variantStyle === 'chips') {
                var parts = item.variant_title.split(' / ');
                variantHtml = '<div class="cart-drawer__variant-chips">' +
                  parts.map(function (p) {
                    return '<span class="cart-drawer__variant-chip">' + escapeHtml(p.trim()) + '</span>';
                  }).join('') + '</div>';
              } else {
                variantHtml = '<p class="cart-drawer__line-variant">' + escapeHtml(item.variant_title) + '</p>';
              }
            }
          }
          li.innerHTML =
            '<div class="cart-drawer__line-image">' + imgHtml + '</div>' +
            '<div>' +
              '<p class="cart-drawer__line-title">' + escapeHtml(item.product_title) + '</p>' +
              variantHtml +
              '<div class="cart-drawer__line-controls">' +
                '<div class="cart-drawer__line-qty">' +
                  '<button type="button" data-line-dec aria-label="−">−</button>' +
                  '<span data-line-qty>' + item.quantity + '</span>' +
                  '<button type="button" data-line-inc aria-label="+">+</button>' +
                '</div>' +
                '<div class="cart-drawer__line-prices">' +
                  pricesHtml +
                '</div>' +
              '</div>' +
            '</div>' +
            '<button class="cart-drawer__line-remove" data-line-remove aria-label="Entfernen">' +
              '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/></svg>' +
            '</button>';
          // Snapshot quantity at render time. Subsequent clicks read the live
          // value off the DOM so rapid +/+/+ doesn't desync.
          var key = item.key;
          function changeByKey (delta, absolute) {
            if (li._busy) return;
            li._busy = true;
            li.classList.add('is-loading');
            // Read current qty from the live cart, not the captured `item`.
            fetchCart().then(function (cart2) {
              var fresh = null;
              for (var i = 0; i < cart2.items.length; i++) {
                if (cart2.items[i].key === key) { fresh = cart2.items[i]; break; }
              }
              var nextQty = absolute != null ? absolute : ((fresh ? fresh.quantity : 0) + delta);
              if (nextQty < 0) nextQty = 0;
              return fetch('/cart/update.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body: JSON.stringify({ updates: (function () { var u = {}; u[key] = nextQty; return u; })() })
              }).then(parseAndTagStatus);
            })
              .then(function () { return fetchCart().then(renderWithCompareAt); })
              .catch(function () {})
              .then(function () {
                li._busy = false;
                li.classList.remove('is-loading');
              });
          }
          li.querySelector('[data-line-dec]').addEventListener('click', function () {
            changeByKey(-1);
          });
          li.querySelector('[data-line-inc]').addEventListener('click', function () {
            changeByKey(1);
          });
          li.querySelector('[data-line-remove]').addEventListener('click', function () {
            changeByKey(0, 0);
          });
          lines.appendChild(li);
        });
      }

      if (protectionEl && protectionVariantId) {
        protectionEl.removeAttribute('hidden');
        var on = protectionIdx > -1;
        protectionEl.classList.toggle('is-on', on);
        if (protectionToggle && protectionToggle !== protectionEl) {
          protectionToggle.classList.toggle('is-on', on);
        }
      }

      var subtotalCents = realItems.reduce(function (s, i) { return s + i.final_line_price; }, 0);
      if (protectionIdx > -1) {
        var protItem = cart.items[protectionIdx - 1];
        if (protItem) subtotalCents += protItem.final_line_price;
      }
      if (subtotalEl) subtotalEl.textContent = formatMoney(subtotalCents, moneyFormat);

      // Total Gespart: sum of (max(compare_at × qty, original_line) - final_line).
      // Mirrors per-line strikethrough logic above so the line totals always
      // add up to the displayed Gespart amount.
      var savings = realItems.reduce(function (s, i) {
        var compareAt = getCompareAtCents(i);
        var compareLine = compareAt * (i.quantity || 1);
        var origLine = Math.max(compareLine, i.original_line_price || 0);
        var diff = origLine - (i.final_line_price || 0);
        return s + (diff > 0 ? diff : 0);
      }, 0);
      if (savingsRow && savingsAmount) {
        if (savings > 0) {
          savingsRow.removeAttribute('hidden');
          savingsAmount.textContent = '−' + formatMoney(savings, moneyFormat);
        } else {
          savingsRow.setAttribute('hidden', '');
        }
      }
    }

    // If a previous protection-add attempt blew up (invalid variant id, etc.)
    // we remember it and stop auto-adding for this session — otherwise every
    // open() would re-trigger the same bad request and break rendering.
    var protectionBroken = false;

    function renderWithCompareAt (cart) {
      if (!cart || !cart.items) { render(cart); return; }
      // Render immediately (fast UI) then re-render once compare_at data lands.
      render(cart);
      ensureVariantInfo(cart.items).then(function () {
        // Only re-render if still mounted
        if (document.body.contains(drawer)) render(cart);
      });
    }

    function refresh () {
      fetchCart().then(function (cart) {
        if (!cart || !cart.items) return;
        var needsProtection = protectionVariantId &&
                              protectionDefaultOn &&
                              !protectionBroken &&
                              findProtectionLineIndex(cart) === -1 &&
                              cart.items.length > 0;
        if (!needsProtection) {
          renderWithCompareAt(cart);
          return;
        }
        cartAdd(protectionVariantId, 1)
          .then(function (res) {
            if (res && res._httpOk === false) {
              protectionBroken = true;
              console.warn('[cart-drawer] Auto-add of shipping protection failed — disabling for this session.', res);
            }
          })
          .catch(function () { protectionBroken = true; })
          // Always re-fetch the full cart and render it, regardless of whether
          // protection-add succeeded. /cart/add.js returns the line item only,
          // not a cart object, so we cannot render its response directly.
          .then(function () { return fetchCart(); })
          .then(function (fresh) {
            if (fresh && fresh.items) renderWithCompareAt(fresh);
            else renderWithCompareAt(cart);
          })
          .catch(function () { renderWithCompareAt(cart); });
      }).catch(function () {});
    }

    if (protectionToggle) {
      var pendingToggle = false;
      protectionToggle.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (!protectionVariantId) return;
        if (pendingToggle) return;
        pendingToggle = true;

        var wasOn = protectionEl.classList.contains('is-on');
        var willBeOn = !wasOn;
        protectionEl.classList.toggle('is-on', willBeOn);

        fetchCart()
          .then(function (cart) {
            // Be very defensive when removing the protection line.
            // We only remove it via /cart/update.js with the EXACT line key,
            // never by index — that way a stale index can never wipe out a
            // real product line if the cart shifted between fetches.
            var idx = findProtectionLineIndex(cart);
            if (willBeOn && idx === -1) {
              try { localStorage.setItem('shippingProtectionPref', '1'); } catch (e) {}
              return cartAdd(protectionVariantId, 1);
            }
            if (!willBeOn && idx > -1) {
              var protItem = cart.items[idx - 1];
              try { localStorage.setItem('shippingProtectionPref', '0'); } catch (e) {}
              if (protItem && protItem.key) {
                // Remove by line-item key, not by line number — bullet-proof
                // against any reordering.
                return fetch('/cart/update.js', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                  body: JSON.stringify({ updates: (function () {
                    var u = {}; u[protItem.key] = 0; return u;
                  })() })
                }).then(parseAndTagStatus);
              }
              return cartChange(idx, 0);
            }
            return cart;
          })
          .then(function (result) {
            var failed = result && (result._httpOk === false || result.errors);
            if (failed) {
              protectionEl.classList.toggle('is-on', wasOn);
              return;
            }
            return fetchCart().then(renderWithCompareAt);
          })
          .catch(function () { protectionEl.classList.toggle('is-on', wasOn); })
          .then(function () { pendingToggle = false; });
      });
    }

    function bindCartOpeners () {
      document.querySelectorAll('[data-cart-open]').forEach(function (el) {
        if (el._cartOpenBound) return;
        el._cartOpenBound = true;
        el.addEventListener('click', function (e) { e.preventDefault(); open(); });
      });
    }
    bindCartOpeners();
    // Custom event: lets external code (e.g. multi-add) open the cart drawer
    document.addEventListener('sanuro:cart:open', open);
    drawer.querySelectorAll('[data-cart-close]').forEach(function (el) {
      el.addEventListener('click', close);
    });
    if (backdrop) backdrop.addEventListener('click', close);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
    });

    document.addEventListener('submit', function (e) {
      var form = e.target.closest && e.target.closest('form[action*="/cart/add"]');
      if (!form) return;
      e.preventDefault();

      // Collect any active FBT toggles in this product section so they get
      // added together with the main product.
      var fbtItems = (window._sanuroCollectFbtItems ? window._sanuroCollectFbtItems(form) : []);

      if (fbtItems.length) {
        // Build a single items[] batch: main product + FBT extras.
        var idInput = form.querySelector('input[name="id"]');
        var qtyInput = form.querySelector('[name="quantity"]');
        var mainId = idInput ? parseInt(idInput.value, 10) : 0;
        var mainQty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
        var items = [];
        if (mainId) items.push({ id: mainId, quantity: mainQty });
        fbtItems.forEach(function (it) { items.push(it); });
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: items })
        })
          .then(function (r) { return r.json(); })
          .then(function () { open(); })
          .catch(function () { form.submit(); });
        return;
      }

      var data = new FormData(form);
      fetch('/cart/add.js', { method: 'POST', body: data, headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function () { open(); })
        .catch(function () { form.submit(); });
    });

    fetchCart().then(renderWithCompareAt).catch(function () {});

    /* Reservation timer */
    var timerDisplay = drawer.querySelector('[data-timer-display]');
    if (timerDisplay) {
      var startKey = 'cartReservationStart';
      var DURATION = 5 * 60 + 30;
      var start = 0;
      try { start = parseInt(sessionStorage.getItem(startKey), 10) || 0; } catch (e) {}
      if (!start) {
        start = Math.floor(Date.now() / 1000);
        try { sessionStorage.setItem(startKey, String(start)); } catch (e) {}
      }
      function tick () {
        if (!document.body.contains(timerDisplay)) return;
        var elapsed = Math.floor(Date.now() / 1000) - start;
        var left = Math.max(0, DURATION - elapsed);
        var m = Math.floor(left / 60);
        var s = left % 60;
        timerDisplay.textContent = (m < 10 ? '0' : '') + m + ':' + (s < 10 ? '0' : '') + s;
        if (left <= 0) {
          start = Math.floor(Date.now() / 1000);
          try { sessionStorage.setItem(startKey, String(start)); } catch (e) {}
        }
      }
      tick();
      // Drawer node lives in the layout; clear interval when it is removed.
      safeInterval(drawer, tick, 1000);
    }
  }

  /* ============================================================
     Collection toolbar (filters + sort)
     ============================================================ */
  function initCollectionToolbar (root) {
    (root || document).querySelectorAll('[data-collection-toolbar]').forEach(function (bar) {
      if (alreadyInit(bar, 'colltoolbar')) return;

      var sortForm = bar.querySelector('[data-collection-sort]');
      if (sortForm) {
        var sortSelect = sortForm.querySelector('select[data-auto-submit]');
        if (sortSelect) {
          listen(bar, sortSelect, 'change', function () { sortForm.submit(); });
        }
      }

      bar.querySelectorAll('[data-collection-filter-form]').forEach(function (form) {
        listen(bar, form, 'submit', function () { /* default GET submit ok */ });
      });

      // Close any open filter dropdown when clicking outside
      listen(bar, document, 'click', function (e) {
        bar.querySelectorAll('details.cf-dropdown[open]').forEach(function (d) {
          if (!d.contains(e.target)) d.removeAttribute('open');
        });
      });
    });
  }

  /* ============================================================
     Reviews section — mobile slider variant
     Activates only on phone widths (<=900px), reuses generic swipe handler.
     ============================================================ */
  function initReviewsMobileSlider (root) {
    (root || document).querySelectorAll('[data-reviews-section]').forEach(function (sect) {
      if (alreadyInit(sect, 'reviewsMobile')) return;
      var mode = sect.getAttribute('data-mobile-layout');
      if (mode !== 'slider') return;
      var track = sect.querySelector('[data-reviews-track]');
      var slides = sect.querySelectorAll('.review-card');
      var dotsWrap = sect.querySelector('[data-reviews-section-dots]');
      if (!track || slides.length <= 1) return;

      var current = 0;

      function isMobile () { return window.matchMedia('(max-width: 900px)').matches; }

      function show (i) {
        if (i < 0) i = slides.length - 1;
        if (i >= slides.length) i = 0;
        current = i;
        if (isMobile()) {
          track.style.transform = 'translateX(' + (-i * 100) + '%)';
        } else {
          track.style.transform = '';
        }
        if (dotsWrap) {
          dotsWrap.querySelectorAll('.reviews__dot').forEach(function (d, di) {
            d.classList.toggle('is-active', di === i);
          });
        }
      }

      if (dotsWrap) {
        dotsWrap.innerHTML = '';
        slides.forEach(function (_s, i) {
          var dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'reviews__dot' + (i === 0 ? ' is-active' : '');
          dot.setAttribute('aria-label', 'Bewertung ' + (i + 1));
          listen(sect, dot, 'click', function () { show(i); });
          dotsWrap.appendChild(dot);
        });
      }

      // Reset transform on resize back to desktop
      listen(sect, window, 'resize', function () {
        if (!isMobile()) track.style.transform = '';
        else show(current);
      });

      // Swipe support — only on mobile widths
      var startX = 0, startY = 0, deltaX = 0, isDragging = false, axis = null;
      function onStart (e) {
        if (!isMobile()) return;
        var t = e.touches ? e.touches[0] : e;
        startX = t.clientX; startY = t.clientY;
        deltaX = 0; axis = null; isDragging = true;
        sect.classList.add('is-dragging');
      }
      function onMove (e) {
        if (!isDragging) return;
        var t = e.touches ? e.touches[0] : e;
        deltaX = t.clientX - startX;
        var dY = t.clientY - startY;
        if (!axis) {
          if (Math.abs(deltaX) > 8 || Math.abs(dY) > 8) {
            axis = Math.abs(deltaX) > Math.abs(dY) ? 'x' : 'y';
          }
        }
        if (axis === 'x') {
          if (e.cancelable && e.touches) e.preventDefault();
          var w = sect.clientWidth || 1;
          var pct = (deltaX / w) * 100;
          track.style.transform = 'translateX(' + (-current * 100 + pct) + '%)';
        }
      }
      function onEnd () {
        if (!isDragging) return;
        isDragging = false;
        sect.classList.remove('is-dragging');
        if (axis === 'x') {
          var w = sect.clientWidth || 1;
          var threshold = w * 0.18;
          if (deltaX > threshold) show(current - 1);
          else if (deltaX < -threshold) show(current + 1);
          else show(current);
        }
      }
      listen(sect, sect, 'touchstart', onStart, { passive: true });
      listen(sect, sect, 'touchmove',  onMove,  { passive: false });
      listen(sect, sect, 'touchend',   onEnd);
      listen(sect, sect, 'touchcancel', onEnd);
    });
  }

  /* ============================================================
     Cart page (/cart) — AJAX qty change & remove
     Document-level delegation so it survives any section reload
     and works regardless of how Shopify wraps the section.
     ============================================================ */
  function initCartPage () {
    if (alreadyInit(document, 'cartPage')) return;

    function moneyFormat () {
      var drawer = document.querySelector('[data-cart-drawer]');
      if (drawer) return drawer.getAttribute('data-money-format') || 'CHF {{amount}}';
      return 'CHF {{amount}}';
    }

    function cartChange (line, qty) {
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ line: line, quantity: qty })
      }).then(function (r) { return r.json().then(function (d) { d._ok = r.ok; return d; }); });
    }

    function getCartPage () {
      // Find the cart-page container (could be wrapped in different shopify-section divs).
      return document.querySelector('.cart-template');
    }

    function refreshUI (cart) {
      var page = getCartPage();
      if (!page || !cart || !cart.items) return;

      // If item count zero, reload to get the empty-state markup
      if (cart.items.length === 0) { window.location.reload(); return; }

      // Re-fetch the cart page section via Section Rendering API so the
      // server-side Liquid recomputes strikethrough RRP + Gespart correctly.
      // Falls back to a partial JS update if section rendering fails.
      var sectionId = page.closest('.shopify-section');
      if (sectionId && sectionId.id) {
        var id = sectionId.id.replace('shopify-section-', '');
        fetch('/cart?section_id=' + encodeURIComponent(id), {
          headers: { 'Accept': 'text/html' }
        })
          .then(function (r) { return r.ok ? r.text() : null; })
          .then(function (html) {
            if (!html) { partialUpdate(cart); return; }
            var temp = document.createElement('div');
            temp.innerHTML = html;
            var fresh = temp.querySelector('.cart-template');
            if (!fresh) { partialUpdate(cart); return; }
            page.innerHTML = fresh.innerHTML;
            // Update cart count badges everywhere
            document.querySelectorAll('[data-cart-count]').forEach(function (el) {
              el.textContent = cart.item_count;
            });
          })
          .catch(function () { partialUpdate(cart); });
      } else {
        partialUpdate(cart);
      }
    }

    function partialUpdate (cart) {
      var page = getCartPage();
      if (!page) return;
      var rows = page.querySelectorAll('[data-cart-line]');
      var keysSeen = {};
      cart.items.forEach(function (item) { keysSeen[item.key] = true; });

      rows.forEach(function (row) {
        var key = row.getAttribute('data-cart-key');
        if (!keysSeen[key]) { row.remove(); return; }
        var item = null, lineNumber = 0;
        for (var i = 0; i < cart.items.length; i++) {
          if (cart.items[i].key === key) { item = cart.items[i]; lineNumber = i + 1; break; }
        }
        if (!item) return;
        row.setAttribute('data-cart-line', String(lineNumber));
        var input = row.querySelector('[data-cart-input]');
        if (input && document.activeElement !== input) input.value = item.quantity;
        var lt = row.querySelector('[data-cart-line-total]');
        if (lt) {
          // Preserve strikethrough span if present
          var compareEl = lt.querySelector('.cart-item__compare');
          var priceEl = lt.querySelector('.cart-item__price');
          if (priceEl) priceEl.textContent = formatMoney(item.final_line_price, moneyFormat());
          else lt.textContent = formatMoney(item.final_line_price, moneyFormat());
          // We can't reliably recompute compareLine without compare_at_price;
          // leave the strikethrough as-is until the next page load.
          if (compareEl) compareEl.style.display = '';
        }
      });

      var subtotal = page.querySelector('[data-cart-subtotal]');
      if (subtotal) subtotal.textContent = formatMoney(cart.total_price, moneyFormat());

      document.querySelectorAll('[data-cart-count]').forEach(function (el) {
        el.textContent = cart.item_count;
      });
    }

    function changeRow (row, newQty) {
      var line = parseInt(row.getAttribute('data-cart-line'), 10) || 1;
      if (row._cartBusy) return;
      row._cartBusy = true;
      row.classList.add('is-loading');
      cartChange(line, newQty)
        .then(refreshUI)
        .catch(function () {})
        .then(function () {
          row._cartBusy = false;
          row.classList.remove('is-loading');
        });
    }

    document.addEventListener('click', function (e) {
      if (!e.target || !e.target.closest) return;
      var btn = e.target.closest('[data-cart-dec], [data-cart-inc], [data-cart-remove]');
      if (!btn) return;
      var row = btn.closest('[data-cart-line]');
      if (!row) return;
      // Only handle on the cart page (presence of [data-cart-key] uniquely identifies cart-page rows
      // — drawer lines use a different attribute set).
      if (!row.hasAttribute('data-cart-key')) return;
      e.preventDefault();
      var input = row.querySelector('[data-cart-input]');
      var current = parseInt(input ? input.value : 1, 10) || 0;
      if (btn.matches('[data-cart-remove]')) {
        changeRow(row, 0);
      } else if (btn.matches('[data-cart-dec]')) {
        changeRow(row, Math.max(0, current - 1));
      } else if (btn.matches('[data-cart-inc]')) {
        changeRow(row, current + 1);
      }
    });

    document.addEventListener('change', function (e) {
      if (!e.target || !e.target.closest) return;
      var input = e.target.closest('[data-cart-input]');
      if (!input) return;
      var row = input.closest('[data-cart-line]');
      if (!row || !row.hasAttribute('data-cart-key')) return;
      var qty = parseInt(input.value, 10);
      if (isNaN(qty) || qty < 0) qty = 0;
      changeRow(row, qty);
    });

    // Prevent the default form submit (the legacy "Aktualisieren" path) from
    // duplicating updates if a user hits Enter in an input.
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || !form.classList || !form.classList.contains('cart-form')) return;
      // Allow checkout submit (button name="checkout") through normally
      if (e.submitter && e.submitter.name === 'checkout') return;
      e.preventDefault();
    });
  }

  /* ============================================================
     Frequently Bought Together (FBT)
     ============================================================ */
  // Helper: read all currently-enabled FBT variant inputs anywhere within
  // the closest main-product section of `scope`, returning [{id, quantity}].
  function collectFbtItems (/* scope */) {
    // FBT block is limited to 1 per product page, so a document-wide query is safe.
    var inputs = document.querySelectorAll('[data-fbt-block] [data-fbt-variant]:not([disabled])');
    var items = [];
    inputs.forEach(function (input) {
      var id = parseInt(input.value, 10);
      if (id) items.push({ id: id, quantity: 1 });
    });
    return items;
  }
  // Expose so other handlers (cart drawer, bundle-multi-add) can pull FBT items in.
  window._sanuroCollectFbtItems = collectFbtItems;

  function initFBT (root) {
    // (a) Wire every toggle on every FBT block — independent of the add-all button.
    (root || document).querySelectorAll('[data-fbt-block]').forEach(function (block) {
      if (alreadyInit(block, 'fbtBlock')) return;
      var toggles = block.querySelectorAll('[data-fbt-toggle]');
      toggles.forEach(function (toggle) {
        listen(block, toggle, 'click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          var item = toggle.closest('.fbt__item');
          var variantInput = item && item.querySelector('[data-fbt-variant]');
          var isOn = toggle.classList.toggle('is-on');
          if (item) item.classList.toggle('fbt__item--checked', isOn);
          if (variantInput) variantInput.disabled = !isOn;
        });
      });
    });

    // (b) Wire the optional add-all button.
    (root || document).querySelectorAll('[data-fbt-add-all]').forEach(function (btn) {
      if (alreadyInit(btn, 'fbtAddAll')) return;
      var block = btn.closest('[data-fbt-block]') || btn.closest('.fbt') || btn.parentElement;

      listen(btn, btn, 'click', function () {
        var variantInputs = block.querySelectorAll('[data-fbt-variant]:not([disabled])');
        if (!variantInputs.length) return;
        var items = [];
        variantInputs.forEach(function (input) {
          var id = parseInt(input.value, 10);
          if (id) items.push({ id: id, quantity: 1 });
        });
        if (!items.length) return;

        btn.disabled = true;
        var prevText = btn.textContent;
        btn.textContent = '…';

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items: items })
        })
        .then(function (r) { return r.json(); })
        .then(function () {
          var badge = document.querySelector('[data-cart-count]');
          if (badge) {
            fetch('/cart.js')
              .then(function (r) { return r.json(); })
              .then(function (cart) { badge.textContent = cart.item_count; });
          }
          document.dispatchEvent(new CustomEvent('sanuro:cart:open'));
          var drawerToggle = document.querySelector('[data-cart-toggle]');
          if (drawerToggle) drawerToggle.click();
        })
        .catch(function () {})
        .finally(function () {
          btn.disabled = false;
          btn.textContent = prevText;
        });
      });
    });
  }

  /* ============================================================
     Sticky Add-to-Cart Bar
     ============================================================ */
  function initStickyAtc (root) {
    (root || document).querySelectorAll('[data-sticky-atc]').forEach(function (bar) {
      if (alreadyInit(bar, 'stickyAtc')) return;
      var threshold = parseInt(bar.getAttribute('data-show-after'), 10) || 600;

      function update () {
        if (window.scrollY > threshold) bar.classList.add('is-visible');
        else bar.classList.remove('is-visible');
      }
      update();
      listen(bar, window, 'scroll', update, { passive: true });
    });
  }

  /* ============================================================
     Sticky ATC — variant chip selector
     Toggle: Sticky Add-to-Cart Bar → Content → "Varianten-Auswahl anzeigen"
     ============================================================ */
  function initStickyAtcVariants (root) {
    (root || document).querySelectorAll('[data-satc-variants]').forEach(function (container) {
      if (alreadyInit(container, 'satcVariants')) return;

      var form = container.closest('form');
      if (!form) return;
      var hiddenId = form.querySelector('input[name="id"]');
      if (!hiddenId) return;

      var variantScript = container.querySelector('[data-satc-variants-json]');
      if (!variantScript) return;
      var variants = [];
      try { variants = JSON.parse(variantScript.textContent); } catch (e) { return; }

      function findVariantId (optValues) {
        // First pass: prefer available variant
        for (var vi = 0; vi < variants.length; vi++) {
          var v = variants[vi];
          if (!v.options || v.options.length !== optValues.length) continue;
          var ok = true;
          for (var oi = 0; oi < optValues.length; oi++) {
            if (v.options[oi] !== optValues[oi]) { ok = false; break; }
          }
          if (ok && v.available) return v.id;
        }
        // Second pass: any matching (sold out)
        for (var vi2 = 0; vi2 < variants.length; vi2++) {
          var v2 = variants[vi2];
          if (!v2.options || v2.options.length !== optValues.length) continue;
          var ok2 = true;
          for (var oi2 = 0; oi2 < optValues.length; oi2++) {
            if (v2.options[oi2] !== optValues[oi2]) { ok2 = false; break; }
          }
          if (ok2) return v2.id;
        }
        return null;
      }

      function updateVariant () {
        var optVals = [];
        container.querySelectorAll('.satc-opt-group').forEach(function (group) {
          // Stil 2 — dropdown select
          var sel = group.querySelector('select.satc-select');
          if (sel) {
            optVals.push(sel.value);
          } else {
            // Stil 1 / 3 — radio chips
            var checked = group.querySelector('.satc-chip-input:checked');
            if (checked) optVals.push(checked.value);
          }
        });
        if (!optVals.length) return;
        var id = findVariantId(optVals);
        if (id) hiddenId.value = String(id);
      }

      // Single change listener covers both radios and selects
      listen(container, container, 'change', updateVariant);
      updateVariant(); // initialise on load
    });
  }

  /* ============================================================
     Animated Stats — count up on scroll into view
     ============================================================ */
  function initAnimStats (root) {
    (root || document).querySelectorAll('[data-anim-stats]').forEach(function (section) {
      if (alreadyInit(section, 'animStats')) return;
      var nums = section.querySelectorAll('[data-stat-target]');
      if (!nums.length) return;

      function easeOut (t) { return 1 - Math.pow(1 - t, 3); }

      function animate (el) {
        var raw = el.getAttribute('data-stat-target') || '0';
        var prefix = el.getAttribute('data-stat-prefix') || '';
        var suffix = el.getAttribute('data-stat-suffix') || '';
        var duration = parseInt(el.getAttribute('data-stat-duration'), 10) || 1800;
        // Detect decimal
        var hasDecimal = raw.indexOf('.') !== -1 || raw.indexOf(',') !== -1;
        var target = parseFloat(String(raw).replace(',', '.')) || 0;
        var start = performance.now();

        function step (now) {
          var t = Math.min(1, (now - start) / duration);
          var val = target * easeOut(t);
          var display = hasDecimal ? val.toFixed(1).replace('.', ',') : Math.floor(val).toLocaleString('de-CH');
          el.textContent = prefix + display + suffix;
          if (t < 1) requestAnimationFrame(step);
          else el.textContent = prefix + (hasDecimal ? target.toFixed(1).replace('.', ',') : target.toLocaleString('de-CH')) + suffix;
        }
        requestAnimationFrame(step);
      }

      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              nums.forEach(animate);
              io.disconnect();
            }
          });
        }, { threshold: 0.25 });
        io.observe(section);
        onCleanup(section, function () { io.disconnect(); });
      } else {
        nums.forEach(animate);
      }
    });
  }

  /* ============================================================
     Live Sales Feed
     ============================================================ */
  function initLiveSales (root) {
    (root || document).querySelectorAll('[data-live-sales]').forEach(function (sec) {
      if (alreadyInit(sec, 'lsf')) return;
      var cards = Array.prototype.slice.call(sec.querySelectorAll('[data-lsf-card]'));
      var dots  = Array.prototype.slice.call(sec.querySelectorAll('[data-lsf-dot]'));
      // Animate counter
      var countEl = sec.querySelector('[data-lsf-count]');
      if (countEl) {
        var target = parseInt(countEl.getAttribute('data-lsf-count'), 10) || 0;
        var io2 = new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            if (!e.isIntersecting) return;
            io2.unobserve(countEl);
            var start = performance.now(), dur = 1400;
            function step (t) {
              var p = Math.min(1, (t - start) / dur);
              var eased = 1 - Math.pow(1 - p, 3);
              countEl.textContent = Math.round(target * eased).toLocaleString('de-CH');
              if (p < 1) requestAnimationFrame(step);
            }
            requestAnimationFrame(step);
          });
        }, { threshold: 0.3 });
        io2.observe(countEl);
      }
      if (cards.length <= 1) return;
      var idx = 0;
      var ms  = parseInt(sec.dataset.interval, 10) || 3500;
      function show (i) {
        cards.forEach(function (c, n) { c.classList.toggle('is-active', n === i); });
        dots.forEach(function (d, n) { d.classList.toggle('is-active', n === i); });
      }
      var timer = setInterval(function () { idx = (idx + 1) % cards.length; show(idx); }, ms);
      dots.forEach(function (d, n) {
        listen(sec, d, 'click', function () {
          idx = n; show(idx);
          clearInterval(timer);
          timer = setInterval(function () { idx = (idx + 1) % cards.length; show(idx); }, ms);
        });
      });
      onCleanup(sec, function () { clearInterval(timer); });
    });
  }

  /* ============================================================
     Urgency Countdown (localStorage-persistent)
     ============================================================ */
  function initUrgencyCountdown (root) {
    (root || document).querySelectorAll('[data-ucd]').forEach(function (sec) {
      if (alreadyInit(sec, 'ucd')) return;
      var mins    = parseInt(sec.dataset.ucdMinutes, 10) || 30;
      var key     = sec.dataset.ucdKey || 'ucd_default';
      var restart = (sec.dataset.ucdRestart || 'true') === 'true';
      var durMs   = mins * 60 * 1000;
      var hEl = sec.querySelector('[data-ucd-h]');
      var mEl = sec.querySelector('[data-ucd-m]');
      var sEl = sec.querySelector('[data-ucd-s]');
      if (!hEl || !mEl || !sEl) return;
      function pad (n) { return (Math.max(0, n) < 10 ? '0' : '') + Math.max(0, n); }
      function getEnd () {
        var stored;
        try { stored = parseInt(localStorage.getItem(key), 10); } catch (e) {}
        if (!stored || isNaN(stored) || stored <= Date.now()) {
          if (stored && !restart) return stored;
          stored = Date.now() + durMs;
          try { localStorage.setItem(key, String(stored)); } catch (e) {}
        }
        return stored;
      }
      function tick () {
        var end  = getEnd();
        var diff = end - Date.now();
        if (diff <= 0) {
          if (restart) { try { localStorage.removeItem(key); } catch (e) {} diff = durMs; }
          else { hEl.textContent = '00'; mEl.textContent = '00'; sEl.textContent = '00'; return; }
        }
        var totalSec = Math.floor(diff / 1000);
        hEl.textContent = pad(Math.floor(totalSec / 3600));
        mEl.textContent = pad(Math.floor((totalSec % 3600) / 60));
        sEl.textContent = pad(totalSec % 60);
      }
      tick();
      var t = setInterval(tick, 1000);
      onCleanup(sec, function () { clearInterval(t); });
    });
  }

  /* ============================================================
     Master init/teardown for the customizer
     ============================================================ */
  function initAll (scope) {
    initAnnouncementRotator(scope);
    initMobileNav(scope);
    initProductGallery(scope);
    initBundles(scope);
    initBundleMultiAdd(scope);
    initFeaturedProductVariants(scope);
    initInlineReviews(scope);
    initBeforeAfter(scope);
    initBeforeAfterSlider(scope);
    initMarquees(scope);
    initCollectionToolbar(scope);
    initReviewsMobileSlider(scope);
    initFBT(scope);
    initStickyAtc(scope);
    initStickyAtcVariants(scope);
    initAnimStats(scope);
    initLiveSales(scope);
    initUrgencyCountdown(scope);
    // Document-singletons run once; they're idempotent guards.
    initQuantity();
    initCartPage();
    initCartDrawer();
    initReviewLightbox();
  }

  function bootstrap () {
    initAll(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }

  // Shopify customizer events — re-init the affected DOM, clean up on remove.
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    cleanupSubtree(e.target);
  });
  document.addEventListener('shopify:section:reorder', function (e) {
    cleanupSubtree(e.target);
    initAll(e.target);
  });
  document.addEventListener('shopify:block:select', function (e) {
    // Re-running init is safe (idempotent).
    initAll(e.target);
  });

  // Periodic GC sweep — drop registry entries whose elements were removed
  // by anything other than a Shopify event (manual DOM mutation, etc.).
  // Cheap, runs every 10s, only iterates the registry.
  setInterval(function () {
    var stale = [];
    cleanups.forEach(function (_fns, el) {
      if (!document.body.contains(el)) stale.push(el);
    });
    stale.forEach(runCleanupFor);
  }, 10000);
})();
